/**
 * School-admin roster of mentor engagements (pilot MUST).
 *
 * Uses `GET /api/mentor/engagements` (filtered server-side by school for
 * B2B2C_ADMIN). Rows the mentor has not shared with the school arrive redacted
 * (`mentee_name`/`mentee_email` = null) — they render as "Private mentee" with
 * status and counts only. Shared rows can be opened read-only in the mentor
 * workspace (`_can_read` honours `shared_with_school`).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'expo-router';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { colors } from '@/design-system';
import { useResponsive } from '@/hooks/useResponsive';
import { useTranslation } from '@/hooks/useTranslation';
import { ROUTES } from '@/navigation/routes';
import { MentorEngagementsAPI } from '@/services/api/mentorEngagementsAPI';
import type { EngagementStatus, EngagementSummary } from '@/types/mentorEngagements';

type RosterFilter = 'all' | EngagementStatus;
const FILTERS: RosterFilter[] = ['all', 'active', 'completed', 'archived'];

function Tag({ label, tone = 'neutral', icon }: {
  label: string;
  tone?: 'neutral' | 'success' | 'warning' | 'accent';
  icon?: keyof typeof MaterialIcons.glyphMap;
}) {
  const palette = tone === 'success'
    ? { bg: '#E6F4EA', fg: '#1E7B34' }
    : tone === 'warning'
      ? { bg: '#FFF3D6', fg: '#8A5B00' }
      : tone === 'accent'
        ? { bg: '#E8ECFB', fg: '#3147B7' }
        : { bg: '#F1F3F5', fg: '#4A5568' };
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: palette.bg, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999 }}>
      {icon ? <MaterialIcons name={icon} size={13} color={palette.fg} /> : null}
      <Text style={{ color: palette.fg, fontSize: 12, fontWeight: '700' }}>{label}</Text>
    </View>
  );
}

export function EngagementRosterTab() {
  const { t } = useTranslation();
  const router = useRouter();
  const { isPhone } = useResponsive();
  const [rows, setRows] = useState<EngagementSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'forbidden' | 'generic' | null>(null);
  const [filter, setFilter] = useState<RosterFilter>('all');

  // No per-row overview fan-out: unshared rows would 403.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await MentorEngagementsAPI.list());
      setError(null);
    } catch (err) {
      // `apiRequest` attaches `.status`; axios-style errors carry `.response.status`.
      const error = err as { status?: number; response?: { status?: number } };
      const status = error?.status ?? error?.response?.status;
      setError(status === 403 ? 'forbidden' : 'generic');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const counts = useMemo(() => ({
    all: rows.length,
    active: rows.filter(r => r.status === 'active').length,
    completed: rows.filter(r => r.status === 'completed').length,
    archived: rows.filter(r => r.status === 'archived').length,
    crfFinal: rows.filter(r => r.crf_status === 'final').length,
    shared: rows.filter(r => r.shared_with_school).length,
  }), [rows]);
  const visible = filter === 'all' ? rows : rows.filter(r => r.status === filter);

  const openEngagement = (row: EngagementSummary) => {
    router.push({ pathname: ROUTES.MENTOR_ENGAGEMENT_DETAIL, params: { id: String(row.id) } } as never);
  };

  const statusTone = (status: EngagementStatus) =>
    status === 'active' ? 'accent' : status === 'completed' ? 'success' : 'neutral';

  return (
    <View testID="roster_tab" style={{ gap: 16 }}>
      <View style={{ gap: 4 }}>
        <Text className="text-xl font-bold text-foreground">{t('schoolManagement.engagementRoster.title')}</Text>
        <Text className="text-sm text-muted-foreground">{t('schoolManagement.engagementRoster.subtitle')}</Text>
      </View>

      <Card>
        <CardContent className="p-4" style={{ gap: 6 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <MaterialIcons name="lock-outline" size={18} color={colors.primary} />
            <Text className="text-sm font-semibold text-foreground" style={{ flex: 1 }}>
              {t('schoolManagement.engagementRoster.privacyTitle')}
            </Text>
          </View>
          <Text testID="roster_privacy_note" className="text-sm text-muted-foreground">
            {t('schoolManagement.engagementRoster.privacyBody')}
          </Text>
        </CardContent>
      </Card>

      {loading ? (
        <View style={{ padding: 32, alignItems: 'center' }}><ActivityIndicator size="large" color={colors.primary} /></View>
      ) : error ? (
        <Card>
          <CardContent testID="roster_error" className="p-6 items-center" style={{ gap: 12 }}>
            <MaterialIcons name={error === 'forbidden' ? 'lock' : 'error-outline'} size={28} color={colors.primary} />
            <Text accessibilityRole="alert" className="text-sm text-foreground text-center">
              {t(error === 'forbidden' ? 'schoolManagement.engagementRoster.forbidden' : 'schoolManagement.engagementRoster.loadError')}
            </Text>
            <Button testID="roster_retry" variant="outline" onPress={() => void load()}>
              <Text className="text-sm font-semibold">{t('schoolManagement.engagementRoster.retry')}</Text>
            </Button>
          </CardContent>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent testID="roster_empty" className="p-8 items-center" style={{ gap: 8 }}>
            <MaterialIcons name="school" size={32} color={colors.primary} />
            <Text className="text-base font-semibold text-foreground">{t('schoolManagement.engagementRoster.empty')}</Text>
            <Text className="text-sm text-muted-foreground text-center">{t('schoolManagement.engagementRoster.emptyBody')}</Text>
          </CardContent>
        </Card>
      ) : (
        <>
          <View testID="roster_counts" style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            <Tag label={t('schoolManagement.engagementRoster.countActive', { count: counts.active })} tone="accent" />
            <Tag label={t('schoolManagement.engagementRoster.countCompleted', { count: counts.completed })} tone="success" />
            <Tag label={t('schoolManagement.engagementRoster.countCrfFinal', { count: counts.crfFinal })} />
            <Tag label={t('schoolManagement.engagementRoster.countShared', { count: counts.shared })} icon="visibility" />
          </View>

          <View testID="roster_filter" style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {FILTERS.map((value) => {
              const selected = value === filter;
              const label = t(`schoolManagement.engagementRoster.filter${value.charAt(0).toUpperCase()}${value.slice(1)}`);
              return (
                <Pressable
                  key={value}
                  testID={`roster_filter_${value}`}
                  onPress={() => setFilter(value)}
                  accessibilityRole="button"
                  accessibilityState={{ selected }}
                  aria-selected={selected}
                  style={{
                    paddingVertical: 7, paddingHorizontal: 12, borderRadius: 999, borderWidth: 1,
                    borderColor: selected ? colors.primary : '#E2E8F0',
                    backgroundColor: selected ? '#E8ECFB' : '#FFFFFF',
                  }}
                >
                  <Text style={{ fontSize: 12, fontWeight: selected ? '800' : '600', color: selected ? colors.primary : '#4A5568' }}>
                    {label} ({counts[value]})
                  </Text>
                </Pressable>
              );
            })}
          </View>

          {visible.length === 0 ? (
            <Card><CardContent testID="roster_empty_filtered" className="p-6 items-center">
              <Text className="text-sm text-muted-foreground">{t('schoolManagement.engagementRoster.emptyFiltered')}</Text>
            </CardContent></Card>
          ) : null}

          <View style={{ gap: 10 }}>
            {visible.map((row) => {
              const shared = Boolean(row.shared_with_school);
              return (
                <Card key={row.id}>
                  <CardContent testID={`roster_row_${row.id}`} className="p-4" style={{ gap: 10 }}>
                    <View style={{ flexDirection: isPhone ? 'column' : 'row', alignItems: isPhone ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 8 }}>
                      <View style={{ gap: 2, flexShrink: 1 }}>
                        <Text
                          testID={shared ? `roster_mentee_${row.id}` : `roster_private_${row.id}`}
                          className="text-base font-semibold text-foreground"
                          style={!shared ? { fontStyle: 'italic', color: '#718096' } : undefined}
                        >
                          {row.mentee_name || t('schoolManagement.engagementRoster.privateMentee')}
                        </Text>
                        <Text className="text-xs text-muted-foreground">
                          {t('schoolManagement.engagementRoster.mentor', { id: row.mentor_user_id ?? '—' })}
                          {shared && row.mentee_email ? ` · ${row.mentee_email}` : ''}
                        </Text>
                      </View>
                      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                        <Tag label={row.target_level.toUpperCase()} tone="accent" />
                        <Tag label={t(`mentorEngagements.status.${row.status}`)} tone={statusTone(row.status)} />
                        <Tag
                          label={t(shared ? 'schoolManagement.engagementRoster.shared' : 'schoolManagement.engagementRoster.notShared')}
                          tone={shared ? 'success' : 'neutral'}
                          icon={shared ? 'visibility' : 'visibility-off'}
                        />
                      </View>
                    </View>

                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                      <Tag label={t('schoolManagement.engagementRoster.sessions', { count: row.sessions_count })} />
                      <Tag label={t('schoolManagement.engagementRoster.finalSofs', { count: row.sofs_final_count })} />
                      <Tag
                        label={t(row.crf_status === 'final'
                          ? 'schoolManagement.engagementRoster.crfFinal'
                          : row.crf_status === 'draft'
                            ? 'schoolManagement.engagementRoster.crfDraft'
                            : 'schoolManagement.engagementRoster.crfNone')}
                        tone={row.crf_status === 'final' ? 'success' : 'neutral'}
                      />
                      <Tag
                        label={t(row.rules_ok ? 'schoolManagement.engagementRoster.rulesOk' : 'schoolManagement.engagementRoster.rulesIncomplete')}
                        tone={row.rules_ok ? 'success' : 'warning'}
                        icon={row.rules_ok ? 'check-circle' : 'warning-amber'}
                      />
                    </View>

                    {shared ? (
                      <View style={{ flexDirection: 'row' }}>
                        <Button testID={`roster_open_${row.id}`} variant="outline" size="sm" onPress={() => openEngagement(row)}>
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                            <MaterialIcons name="open-in-new" size={15} color={colors.primary} />
                            <Text className="text-sm font-semibold" style={{ color: colors.primary }}>
                              {t('schoolManagement.engagementRoster.open')}
                            </Text>
                          </View>
                        </Button>
                      </View>
                    ) : null}
                  </CardContent>
                </Card>
              );
            })}
          </View>
        </>
      )}
    </View>
  );
}

export default EngagementRosterTab;
